let alertConfig: E3D.IAlertConfig | undefined;

/**
 * Stores the alert configuration at module level so that both
 * E3DRuntime and state machines (toast, confirm-dialog) can access it
 * without circular dependencies.
 */
export function setAlertConfig(config: E3D.IAlertConfig) {
  alertConfig = config;
}

export function getAlertConfig(): E3D.IAlertConfig | undefined {
  return alertConfig;
}

/** Reset for testing purposes. */
export function resetAlertConfig() {
  alertConfig = undefined;
}

/**
 * Matches a glob pattern against a value.
 * Supports `*` as a wildcard that matches any number of characters.
 */
export function matchGlob(pattern: string, value: string): boolean {
  // Escape regex special characters, then replace * with .*
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp('^' + escaped.replace(/\*/g, '.*') + '$');
  return regex.test(value);
}

/**
 * Determines whether an alert with the given ID should be shown,
 * based on an optional filter configuration.
 *
 * - No config → show all (default behavior preserved)
 * - Blacklist mode → show unless ID matches a pattern
 * - Whitelist mode → show only if ID matches a pattern
 */
export function shouldShowAlert(id: string, config?: E3D.IAlertFilterConfig): boolean {
  if (!config) return true;
  const matches = config.ids.some((pattern) => matchGlob(pattern, id));
  return config.mode === 'blacklist' ? !matches : matches;
}

let alertInterceptor: E3D.AlertInterceptor | undefined;

/**
 * Registers a callback that is invoked whenever a toast or dialog is about to be shown.
 * The interceptor receives the full alert payload and can return `false` to prevent the built-in UI.
 * Used by E3DRuntime to re-emit alert events to integrators.
 */
export function setAlertInterceptor(fn: E3D.AlertInterceptor) {
  alertInterceptor = fn;
}

/**
 * Calls the registered interceptor with the alert event.
 * Returns `true` to proceed with the built-in UI, `false` to suppress it.
 * If no interceptor is registered, always returns `true`.
 */
export function interceptAlert(event: E3D.IToastAlertEvent | E3D.IDialogAlertEvent): boolean {
  if (alertInterceptor) {
    return alertInterceptor(event) !== false;
  }
  return true;
}

/** Reset for testing purposes. */
export function resetAlertInterceptor() {
  alertInterceptor = undefined;
}
